import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import io from 'socket.io-client';

const DEBUG = true;


const ElevatorControl = () => {
  const socketRef = useRef(null);
  const [elevatorState, setElevatorState] = useState({
    currentFloor: null,
    targetFloor: null,
    doorState: null,
    movingState: null,
    direction: null,
    isOperational: true,
    servingMode: null,
    lastAction: {
      source: null,
      destination: null,
      origin: null,
      sessionId: null
    }
  });

  const [isConnected, setIsConnected] = useState(false);

  const [callInProgress, setCallInProgress] = useState(false);
  const [callError, setCallError] = useState(null);

  // Přidáme stav pro viditelnost stránky
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);

  // Přidáme debug log pro sledování aktualizací
    const logStateUpdate = (type, data) => {
        console.log(`[${new Date().toISOString()}] ${type}:`, data);
      };

      useEffect(() => {
        const handleVisibilityChange = () => {
          setIsPageVisible(!document.hidden);
        };

        // Přidáme listener pro změnu viditelnosti
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
      }, []);

  // Přesuneme socket setup mimo useEffect pro lepší debug
  const setupSocket = () => {
    const socket = io('https://portal.qrentry.cz:5000', {
      path: '/socket.io',
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000
    });

    socket.on('connect', () => {
      console.log('Connected to elevator server');
      setIsConnected(true);
      socket.emit('request_initial_state');
    });

    // Klíčová změna - přidáme force update
    socket.on('elevator_state', (newState) => {
      console.log('Received state update:', newState);
      // Použijeme callback formu setState pro zajištění aktuálnosti
      setElevatorState(currentState => {
        const updatedState = {
          ...currentState,
          ...newState
        };
        console.log('Updating state to:', updatedState);
        return updatedState;
      });
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from elevator server');
      setIsConnected(false);
    });

    return socket;
  };

  useEffect(() => {
    console.log('Setting up WebSocket connection...');
    const socket = setupSocket();
    socketRef.current = socket;

    // Cleanup
    return () => {
      console.log('Cleaning up WebSocket connection...');
      if (socket) {
        socket.off('elevator_state');
        socket.off('connect');
        socket.off('disconnect');
        socket.disconnect();
      }
    };
  }, []); // Empty dependency array - run once

  // Přidáme useEffect pro sledování změn stavu
  useEffect(() => {
    console.log('State updated in component:', elevatorState);
  }, [elevatorState]);

  // Přidáme komponentu pro debug
  const StateDebug = () => (
    <div className="fixed bottom-4 left-4 p-2 bg-black/80 text-white rounded text-xs">
      <pre>{JSON.stringify(elevatorState, null, 2)}</pre>
      <div>Last Update: {new Date().toISOString()}</div>
    </div>
  );

  const getStatusColor = () => {
    if (elevatorState.movingState === 'MOVING') return 'text-blue-600';
    if (elevatorState.doorState === 'OPENING' || elevatorState.doorState === 'CLOSING') return 'text-yellow-600';
    if (elevatorState.movingState === 'STANDING' && elevatorState.doorState === 'CLOSED') return 'text-green-600';
    return 'text-gray-600';
  };

  const connectionStatus = (
    <div className={`fixed bottom-4 right-4 p-2 rounded-full ${
      isConnected ? 'bg-green-500' : 'bg-red-500'
    }`}>
      <div className="w-2 h-2 rounded-full bg-white" />
    </div>
  );

  const handleElevatorCall = async (floor, direction) => {
    try {
      setCallInProgress(true);
      setCallError(null);

      const response = await fetch('/api/call-elevator', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          floor,
          direction
        })
      });

      if (!response.ok) {
        throw new Error('Failed to call elevator');
      }

      const data = await response.json();
      console.log('Elevator call response:', data);

      if (data.status === 'success') {
        // Možná chceme zobrazit nějaké potvrzení
      }
    } catch (err) {
      setCallError(err.message);
      console.error('Error calling elevator:', err);
    } finally {
      setCallInProgress(false);
    }
  };

  // Funkce pro určení směru pro dané patro
  const getDirectionForFloor = (targetFloor) => {
    const currentFloor = elevatorState.currentFloor || 1;
    return targetFloor < currentFloor ? 'up' : 'down';
  };

  const renderFloorButton = (floor) => {
    const isCurrentFloor = elevatorState.currentFloor === floor;
    const isTargetFloor = elevatorState.targetFloor === floor;
    const direction = getDirectionForFloor(floor);

    return (
      <Button
        key={floor}
        variant={isCurrentFloor ? "secondary" : "outline"}
        className={`relative ${isTargetFloor ? 'border-blue-500' : ''} ${
          isCurrentFloor ? 'bg-blue-100' : ''
        }`}
        onClick={() => handleElevatorCall(floor, direction)}
        disabled={callInProgress || isCurrentFloor}
      >
        <span className="flex items-center gap-2">
          Floor {floor}
          {callInProgress && <span className="animate-spin">⌛</span>}
        </span>
        {isTargetFloor && (
          <span className="absolute right-2 w-2 h-2 rounded-full animate-pulse bg-blue-500" />
        )}
      </Button>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Control Panel</CardTitle>
          <CardDescription>
            Current floor: {elevatorState.currentFloor || '-'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {[1, 2, 3, 4].map(renderFloorButton)}
            </div>
            
            {callError && (
              <div className="p-2 text-sm text-red-600 bg-red-50 rounded">
                Error: {callError}
              </div>
            )}

            {callInProgress && (
              <div className="p-2 text-sm text-blue-600 bg-blue-50 rounded flex items-center justify-center gap-2">
                <span className="animate-spin">⌛</span>
                Calling elevator...
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            Status Monitor
            <span className={`inline-block w-3 h-3 rounded-full ${
              isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
            }`} />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between p-2 bg-gray-100 rounded">
            <span>Current Floor</span>
            <span className="font-medium">{elevatorState.currentFloor || '-'}</span>
          </div>
          <div className="flex justify-between p-2 bg-gray-100 rounded">
            <span>Target Floor</span>
            <span className="font-medium">{elevatorState.targetFloor || '-'}</span>
          </div>
          <div className="flex justify-between p-2 bg-gray-100 rounded">
            <span>Door Status</span>
            <span className={`font-medium capitalize ${
              elevatorState.doorState === 'OPENING' || elevatorState.doorState === 'CLOSING' 
                ? 'text-yellow-600' 
                : elevatorState.doorState === 'OPENED' 
                  ? 'text-green-600' 
                  : 'text-gray-600'
            }`}>
              {elevatorState.doorState || 'Unknown'}
            </span>
          </div>
          <div className="flex justify-between p-2 bg-gray-100 rounded">
            <span>Movement</span>
            <span className={`font-medium ${getStatusColor()}`}>
              {elevatorState.movingState || 'Unknown'}
            </span>
          </div>
          <div className="flex justify-between p-2 bg-gray-100 rounded">
            <span>Direction</span>
            <span className="font-medium capitalize">{elevatorState.direction || '-'}</span>
          </div>
          <div className="flex justify-between p-2 bg-gray-100 rounded">
            <span>Operation Mode</span>
            <span className="font-medium capitalize">
              {elevatorState.servingMode?.toLowerCase().replace('_', ' ') || '-'}
            </span>
          </div>
          {!elevatorState.isOperational && (
            <div className="flex justify-between p-2 bg-red-100 rounded">
              <span>Status</span>
              <span className="text-red-600 font-medium">Fault Active</span>
            </div>
          )}
          {elevatorState.lastAction.sessionId && (
            <Card className="mt-4 bg-blue-50 border-blue-100">
              <CardHeader className="p-4">
                <CardTitle className="text-sm">Last Action</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Session ID:</span>
                  <span className="font-mono">{elevatorState.lastAction.sessionId}</span>
                </div>
                <div className="flex justify-between">
                  <span>From Floor:</span>
                  <span>{elevatorState.lastAction.source}</span>
                </div>
                <div className="flex justify-between">
                  <span>To Floor:</span>
                  <span>{elevatorState.lastAction.destination}</span>
                </div>
                <div className="flex justify-between">
                  <span>Call Origin:</span>
                  <span className="capitalize">{elevatorState.lastAction.origin?.toLowerCase() || '-'}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
      {connectionStatus}
      {DEBUG && <StateDebug />}
    </div>
  );
};

export default ElevatorControl;
